import { memo } from "react";

import { BaseModal } from "../BaseModal";
import Transition from "../shared/Transition";
import {
  ExtrasContainer,
  ExtrasText,
  PageContentContainer,
} from "./style";

import { TEXT_COLORS } from "../../constants/design/colors";
import { SPACING } from "../../constants/design/spacing";
import { AccountStateEnum } from "../../contexts/AuthContext";
import { getAppShortName, getWalletProviderKind } from "../../config/chains";
import { SDK_KIND_TO_WALLET_ENUM } from "../../utils/wallet/types";
import { ModalKeyEnum } from "../../portal";
import { ButtonThemeEnum } from "../Buttons/styles";
import ExternalLinkText from "../ExternalLinkText";
import GeoblockModal from "../GeoblockModal";
import ManualImportModal from "../ManualImportModal";
import StepButtons from "../StepButtons";
import DepositPendingModal from "../shared/DepositPendingModal";
import EnterPasswordModal from "../shared/EnterPasswordModal";
import ModalOverlayInfo from "../shared/ModalOverlayInfo";
import { OverlayInfoContent } from "../shared/ModalOverlayInfo/style";
import { ConnectWalletPageHeights, pageWidth } from "./helpers";
import { ConnectWalletPageEnum, IConnectWalletModalProps } from "./types";
import { useModalSetup } from "./useModalSetup";
import { useModalPaging } from "./useModalPaging";
import { useModalEffects } from "./useModalEffects";
import { useModalActions } from "./useModalActions";
import { useModalView } from "./useModalView";

function ConnectWalletModal(props: IConnectWalletModalProps) {
  const { show, onHide } = props;

  const s1 = useModalSetup();
  const s2 = useModalPaging({ ...props, ...s1 });
  const s3 = useModalEffects({ ...props, ...s1, ...s2 });
  const s4 = useModalActions({ ...props, ...s1, ...s2, ...s3 });
  const s5 = useModalView({ ...props, ...s1, ...s2, ...s3, ...s4 });

  const {
    isGeoblocked,
    accountSigningKeyState,
    t,
    pageError,
    chainId,
    selectedDepositChain,
    availablePages,
    showImportManuallyModal,
    setShowImportManuallyModal,
    showWalletInfoModal,
    setShowWalletInfoModal,
    pageIndex,
    isLoading,
    pendingDepositTxURL,
    pendingDepositTitle,
    onHideClicked,
    page,
    currentPageTitle,
    pageContent,
    stepInfo,
    onPreviousPage,
    onNextPage,
  } = { ...s1, ...s2, ...s3, ...s4, ...s5 };

  if (pendingDepositTxURL) {
    return (
      <DepositPendingModal
        onHide={onHideClicked}
        show={show}
        title={pendingDepositTitle}
        pendingDepositTxURL={pendingDepositTxURL}
      />
    );
  }

  // If geoblocked, show geoblocked modal
  if (isGeoblocked) {
    return <GeoblockModal show={show} onHide={onHide} />;
  }

  // Just show enter password modal
  if (accountSigningKeyState === AccountStateEnum.REQUIRE_PASSWORD) {
    return <EnterPasswordModal onHide={onHideClicked} show={show} />;
  }

  // On SDK chains the SDK's own modal IS the wallet picker. Hide our frame for
  // the entire CONNECT_WALLET step — not only after `isConnectingToWallet` is
  // set. Waiting for that flag paints our modal first, then Para on top, which
  // is the stacked two-dialog flash. Cancel still closes the whole flow
  // (`useModalActions` → onHideClicked), so we do not need this frame as a
  // fallback. Later steps (sign-in, deposit) keep using BaseModal as before.
  const isSdkChain =
    SDK_KIND_TO_WALLET_ENUM[getWalletProviderKind()] !== undefined;
  const hideBehindSdkModal =
    isSdkChain && page === ConnectWalletPageEnum.CONNECT_WALLET;

  return (
    <>
      <ManualImportModal
        show={showImportManuallyModal}
        onHide={() => setShowImportManuallyModal(false)}
        onImportSuccess={onHideClicked}
      />
      <BaseModal
        id={ModalKeyEnum.CONNECT_WALLET}
        noContentPadding
        onHide={onHideClicked}
        show={show && !hideBehindSdkModal}
        title={currentPageTitle}
      >
        <ModalOverlayInfo
          show={showWalletInfoModal}
          onClose={() => setShowWalletInfoModal(false)}
          height="280px"
        >
          <OverlayInfoContent>
            <span>{t("wallets")}</span>
            <span>{t("wallets_overlay_desc", { appName: getAppShortName() })}</span>
            <ExternalLinkText
              href="https://ethereum.org/en/wallets/"
              target="_blank"
              rel="noreferrer"
              color={TEXT_COLORS.two}
              wrapperStyle={{
                marginTop: SPACING.three,
              }}
            >
              {t("learn_more")}
            </ExternalLinkText>
          </OverlayInfoContent>
        </ModalOverlayInfo>

        <PageContentContainer
          style={{
            width: pageWidth,
            height: `calc(${
              ConnectWalletPageHeights[page] + (pageError ? "40px" : "0px")
            })`,
          }}
        >
          <Transition
            key={page}
            style={{
              transitionDuration: "0.3s",
              height: "100%",
            }}
          >
            {pageContent}
            {pageError && (
              <ExtrasContainer>
                <ExtrasText $type="error">{pageError}</ExtrasText>
              </ExtrasContainer>
            )}
          </Transition>
        </PageContentContainer>
        {page !== ConnectWalletPageEnum.CONNECT_WALLET && (
          <StepButtons
            style={{
              width: pageWidth,
            }}
            info={stepInfo.info}
            rightTitle={stepInfo.nextLabel}
            rightButtonTheme={
              (page === ConnectWalletPageEnum.DEPOSIT ||
                page === ConnectWalletPageEnum.SWITCH_NETWORK) &&
              chainId !== selectedDepositChain
                ? ButtonThemeEnum.HIGHLIGHT
                : undefined
            }
            leftHidden={stepInfo.prevHidden}
            rightHidden={stepInfo.nextHidden}
            onClickLeft={
              pageIndex > 0 && !isLoading && availablePages.indexOf(page) !== 0
                ? onPreviousPage
                : undefined
            }
            onClickRight={!stepInfo.nextDisabled ? onNextPage : undefined}
          />
        )}
      </BaseModal>
    </>
  );
}

export default memo(ConnectWalletModal);
